import { useEffect, useRef, useState } from 'react';

/**
 * Hook per implementare pull-to-refresh su mobile
 * 
 * @param {Function} onRefresh - Callback async da eseguire al refresh
 * @param {Object} options - Opzioni configurazione
 * @returns {Object} - { isRefreshing, pullDistance }
 */
const usePullToRefresh = (onRefresh, options = {}) => {
  const {
    threshold = 80,           // Distanza minima per triggerare refresh
    resistance = 2.5,         // Resistenza al drag (più alto = più difficile)
    enabled = true,           // Se abilitato
    hapticFeedback = true     // Se abilitare feedback haptic
  } = options;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const handleTouchStart = (e) => {
      // Solo se siamo in cima alla pagina
      if (window.scrollY === 0) {
        startY.current = e.touches[0].clientY;
        isDragging.current = true;
      }
    };

    const handleTouchMove = (e) => {
      if (!isDragging.current || isRefreshing) return;

      currentY.current = e.touches[0].clientY;
      const distance = currentY.current - startY.current;

      // Solo se drag verso il basso
      if (distance > 0) {
        // Applica resistenza
        const pulledDistance = Math.min(distance / resistance, threshold * 1.5);
        setPullDistance(pulledDistance);

        // Haptic feedback al raggiungimento threshold
        if (hapticFeedback && pulledDistance >= threshold && window.navigator?.vibrate) {
          window.navigator.vibrate(10);
        }

        // Previeni scroll se stiamo tirando
        if (distance > 10) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = async () => {
      if (!isDragging.current) return;

      isDragging.current = false;

      // Se abbiamo superato la soglia, triggera refresh
      if (pullDistance >= threshold && !isRefreshing) {
        setIsRefreshing(true);
        
        // Haptic feedback finale
        if (hapticFeedback && window.navigator?.vibrate) {
          window.navigator.vibrate([20, 10, 20]);
        }

        try {
          await onRefresh();
        } catch (error) {
          console.error('Error refreshing:', error);
        } finally {
          setIsRefreshing(false);
        }
      }

      // Reset pull distance
      setPullDistance(0);
    };

    // Aggiungi event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, isRefreshing, pullDistance, threshold, resistance, hapticFeedback, onRefresh]);

  return {
    isRefreshing,
    pullDistance,
    shouldRefresh: pullDistance >= threshold
  };
};

export default usePullToRefresh;
